import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Target, TrendingUp, AlertTriangle, CheckCircle2, HelpCircle,
  Zap, Loader2, RefreshCw, Send, ChevronRight, Brain, Calendar, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

interface GoalCascadePanelProps {
  compact?: boolean;
}

interface Action {
  id: string;
  title: string;
  category: string;
  estimated_impact_eur: number;
  effort: string;
  deadline?: string;
  why?: string;
  tool_hint?: string;
}

interface Question {
  id: string;
  question: string;
  why_needed?: string;
  priority: "critical" | "important" | "nice_to_have";
  answered?: boolean;
  answer?: string;
  answered_at?: string;
}

interface LeverageSource {
  source: string;
  amount_eur: number;
  note?: string;
}

interface Cascade {
  id: string;
  next_review_at?: string;
  answers_count?: number;
  metrics: {
    days_total: number;
    months_total: number;
    ca_per_day: number;
    ca_per_working_day: number;
    ca_per_month: number;
    monthly_forecast: {
      label: string;
      days: number;
      target_eur: number;
      target_cumulative_eur: number;
    }[];
  };
  audit: Record<string, unknown>;
  strategy: {
    severity: string;
    gap_eur: number;
    gap_pct: number;
    executive_summary: string;
    actions: Action[];
    missing_questions: Question[];
    capacity_verdict?: string;
    leverage_sources?: LeverageSource[];
  };
}

interface Goal {
  id: string;
  target_eur: number;
  start_date: string;
  end_date: string;
  notes?: string;
}

const SEVERITY_STYLES: Record<string, { color: string; bg: string; label: string; icon: typeof AlertTriangle }> = {
  on_track: { color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "En voie", icon: CheckCircle2 },
  stretched: { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "Tendu", icon: TrendingUp },
  critical: { color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", label: "Critique", icon: AlertTriangle },
  impossible: { color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "Impossible", icon: AlertTriangle },
};

const fmtEur = (n: number | undefined) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function GoalCascadePanel({ compact = false }: GoalCascadePanelProps) {
  const navigate = useNavigate();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [cascade, setCascade] = useState<Cascade | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ target_eur: 1000000, start_date: "2026-04-30", end_date: "2026-06-30", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [answerMap, setAnswerMap] = useState<Record<string, string>>({});
  const [answeringId, setAnsweringId] = useState<string>("");
  const [preparingId, setPreparingId] = useState<string>("");
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);

  const loadGoal = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/goals/current`, { credentials: "include" });
      const d = await r.json();
      setGoal(d.goal);
      setCascade(d.cascade);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { loadGoal(); }, []);

  const submitGoal = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/goals/set`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setGoal(d.goal);
      setCascade(d.cascade);
      setShowForm(false);
      toast.success("Plan cascade généré par Grafton");
    } catch (e) {
      toast.error("Erreur lors de la génération du plan");
    }
    setSubmitting(false);
  };

  const rerun = async () => {
    if (!goal) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/goals/${goal.id}/run-cascade`, { method: "POST", credentials: "include" });
      const d = await r.json();
      setCascade(d.cascade);
      toast.success("Plan régénéré");
    } catch {
      toast.error("Erreur");
    }
    setLoading(false);
  };

  const answer = async (q: Question) => {
    if (!goal || !answerMap[q.id]?.trim()) return;
    setAnsweringId(q.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/goals/${goal.id}/answer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_id: q.id, answer: answerMap[q.id] }),
      });
      const d = await r.json();
      setCascade(d.cascade);
      setAnswerMap(prev => ({ ...prev, [q.id]: "" }));
      toast.success("Réponse intégrée — plan mis à jour");
    } catch {
      toast.error("Erreur");
    }
    setAnsweringId("");
  };

  const prepareAction = async (a: Action) => {
    if (!goal) return;
    setPreparingId(a.id);
    setDraft(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/goals/${goal.id}/prepare-action/${a.id}`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json();
      setDraft(d.draft);
      toast.success("Draft préparé — à valider");
    } catch {
      toast.error("Erreur de préparation");
    }
    setPreparingId("");
  };

  const [launchingId, setLaunchingId] = useState<string>("");

  const launchAction = async (a: Action) => {
    const hint = a.tool_hint;
    setLaunchingId(a.id);

    try {
      // Navigation-based launches — user lands on the right module, pre-filled
      if (hint === "generate_dossier") {
        toast.success("Redirection vers tes dossiers de financement");
        setTimeout(() => navigate(`/financement?action=${encodeURIComponent(a.title)}`), 400);
        return;
      }
      if (hint === "activate_agent") {
        toast.success("Redirection vers NanoCorp pour activer les agents");
        setTimeout(() => navigate("/nanocorp"), 400);
        return;
      }
      if (hint === "raise_funds") {
        toast.success("Redirection vers Lever des fonds");
        setTimeout(() => navigate("/ventes?tab=fundraising"), 400);
        return;
      }

      // Execution-based launches — call the orchestrator to prepare a real action
      if (hint === "draft_email" || hint === "create_crm_task" || hint === "schedule_followup") {
        const intent = `${a.title}. ${a.why || ""}`.trim();
        const r = await fetch(`${BACKEND_URL}/api/orchestrator/execute`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        toast.success(`Action exécutée par Grafton — ${data.tool_calls?.length || 0} tool(s) appelé(s)`);
        // Jump to the Autonome page to show the prepared actions
        setTimeout(() => navigate("/grafton-autonome"), 800);
        return;
      }

      // Fallback : prepare a draft (current behavior)
      await prepareAction(a);
    } catch (e) {
      toast.error("Erreur lors du lancement de l'action");
    }
    setLaunchingId("");
  };

  if (loading && !goal) {
    return (
      <Card className="p-6 flex items-center justify-center" data-testid="goal-cascade-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </Card>
    );
  }

  // ─── No active goal → show setup form ──────────────────────────────────
  if (!goal) {
    return (
      <Card className="p-6 border-primary/30" data-testid="goal-cascade-setup">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
            <Target className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold">Définis ton objectif stratégique</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Entre un montant + une période. Grafton génère un <b>plan cascade</b> :
              CA/jour requis, audit de capacité, gap analysis, actions priorisées et
              questions critiques pour atteindre la cible.
            </p>
            {!showForm ? (
              <Button className="mt-4 gap-2" onClick={() => setShowForm(true)} data-testid="goal-open-form">
                <Zap className="h-4 w-4" /> Configurer l'objectif
              </Button>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Montant cible (€)</label>
                    <Input
                      type="number"
                      value={form.target_eur}
                      onChange={(e) => setForm(f => ({ ...f, target_eur: parseFloat(e.target.value) || 0 }))}
                      data-testid="goal-input-target"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Début</label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                      data-testid="goal-input-start"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Fin</label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                      data-testid="goal-input-end"
                    />
                  </div>
                </div>
                <Textarea
                  placeholder="Notes (contexte, contraintes, stratégie initiale...)"
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  data-testid="goal-input-notes"
                />
                <div className="flex gap-2">
                  <Button onClick={submitGoal} disabled={submitting} className="gap-2" data-testid="goal-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                    Générer le plan cascade
                  </Button>
                  <Button variant="ghost" onClick={() => setShowForm(false)} disabled={submitting}>Annuler</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // ─── Active goal + cascade ─────────────────────────────────────────────
  const s = cascade?.strategy;
  const m = cascade?.metrics;
  const sev = SEVERITY_STYLES[s?.severity || "stretched"];
  const SevIcon = sev.icon;

  return (
    <div className="space-y-4" data-testid="goal-cascade-panel">
      {/* Header */}
      <Card className={`p-5 border ${sev.bg}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center ${sev.color}`}>
              <SevIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Objectif stratégique</p>
                <Badge variant="outline" className={`text-[9px] ${sev.color}`}>{sev.label}</Badge>
              </div>
              <h3 className="text-lg md:text-xl font-bold" data-testid="goal-title">
                {fmtEur(goal.target_eur)} — {goal.start_date} → {goal.end_date}
              </h3>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={rerun} disabled={loading} data-testid="goal-rerun">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Régénérer
          </Button>
        </div>

        {s?.executive_summary && (
          <p className="mt-4 text-sm leading-relaxed bg-background/60 rounded-lg p-3 border border-border/40" data-testid="goal-summary">
            <b>Grafton :</b> {s.executive_summary}
          </p>
        )}

        {/* Progress + next review */}
        {cascade && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-background/60 border border-border/40 px-3 py-2">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Réponses intégrées
              </p>
              <p className="text-base font-bold text-emerald-400" data-testid="answers-count">
                {cascade.answers_count || 0} / {s?.missing_questions?.length || 0}
              </p>
            </div>
            <div className="rounded-lg bg-background/60 border border-border/40 px-3 py-2">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Prochaine revue Grafton
              </p>
              <p className="text-base font-bold text-primary" data-testid="next-review">
                {cascade.next_review_at ? new Date(cascade.next_review_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Metrics + Forecast table */}
      {m && (
        <Card className="p-5" data-testid="goal-metrics">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">Tableau prévisionnel</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MiniStat label="Jours" value={`${m.days_total}`} />
            <MiniStat label="CA / jour" value={fmtEur(m.ca_per_day)} />
            <MiniStat label="CA / jour ouvré" value={fmtEur(m.ca_per_working_day)} />
            <MiniStat label="CA / mois" value={fmtEur(m.ca_per_month)} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                  <th className="py-2">Mois</th>
                  <th className="py-2">Jours</th>
                  <th className="py-2 text-right">Cible mois</th>
                  <th className="py-2 text-right">Cumul</th>
                </tr>
              </thead>
              <tbody>
                {m.monthly_forecast.map((row, i) => (
                  <tr key={i} className="border-b border-border/20 last:border-0">
                    <td className="py-2 font-medium">{row.label}</td>
                    <td className="py-2 text-muted-foreground">{row.days} j</td>
                    <td className="py-2 text-right font-semibold">{fmtEur(row.target_eur)}</td>
                    <td className="py-2 text-right text-primary">{fmtEur(row.target_cumulative_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Gap + Capacity verdict */}
      {s && (
        <Card className="p-5" data-testid="goal-gap">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className={`h-4 w-4 ${sev.color}`} />
            <h4 className="text-sm font-semibold uppercase tracking-wider">Gap & capacité</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <MiniStat label="Gap manquant" value={fmtEur(s.gap_eur)} accent={sev.color} />
            <MiniStat label="% gap" value={`${s.gap_pct?.toFixed?.(1) || 0}%`} accent={sev.color} />
          </div>
          {s.capacity_verdict && (
            <p className="text-sm text-muted-foreground bg-background/60 rounded-lg p-3 border border-border/40">
              {s.capacity_verdict}
            </p>
          )}
        </Card>
      )}

      {/* Leverage sources — positive side: what Grafton can activate to close the gap */}
      {s?.leverage_sources && s.leverage_sources.length > 0 && (
        <Card className="p-5 border-emerald-500/30" data-testid="goal-leverage">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">
              Leviers activables — capacité déjà en place
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {s.leverage_sources.map((l, i) => (
              <div key={i} className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/30">{l.source}</Badge>
                  <span className="text-sm font-bold text-emerald-400">+{fmtEur(l.amount_eur)}</span>
                </div>
                {l.note && <p className="text-xs text-muted-foreground">{l.note}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Actions */}
      {s?.actions && s.actions.length > 0 && (
        <Card className="p-5" data-testid="goal-actions">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">
              Actions priorisées ({s.actions.length})
            </h4>
          </div>
          <div className="space-y-2">
            {s.actions.map(a => (
              <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:border-primary/30 transition-colors" data-testid={`action-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className="text-[9px]">{a.category}</Badge>
                    <Badge variant="outline" className="text-[9px] text-primary">+{fmtEur(a.estimated_impact_eur)}</Badge>
                    <Badge variant="outline" className="text-[9px]">effort {a.effort}</Badge>
                    {a.deadline && <Badge variant="outline" className="text-[9px] text-muted-foreground">{a.deadline}</Badge>}
                    {a.tool_hint && <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/30">→ {a.tool_hint}</Badge>}
                  </div>
                  <p className="text-sm font-semibold">{a.title}</p>
                  {a.why && <p className="text-xs text-muted-foreground mt-0.5">{a.why}</p>}
                </div>
                <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => prepareAction(a)} disabled={preparingId === a.id} data-testid={`prepare-${a.id}`}>
                  {preparingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronRight className="h-3 w-3" />}
                  Préparer
                </Button>
                {a.tool_hint && (
                  <Button size="sm" className="gap-1 shrink-0" onClick={() => launchAction(a)} disabled={launchingId === a.id} data-testid={`launch-${a.id}`}>
                    {launchingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                    Lancer
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Draft preview */}
      {draft && (
        <Card className="p-5 border-primary/40" data-testid="goal-draft">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-primary">Draft préparé</h4>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Fermer</Button>
          </div>
          {typeof draft.title === "string" && <p className="text-sm font-bold mb-2">{draft.title}</p>}
          {typeof draft.subject === "string" && <p className="text-xs text-muted-foreground mb-1">Sujet : {draft.subject}</p>}
          {typeof draft.recipient_hint === "string" && <p className="text-xs text-muted-foreground mb-2">À : {draft.recipient_hint}</p>}
          {typeof draft.body === "string" && (
            <pre className="text-sm bg-background/60 rounded-lg p-3 whitespace-pre-wrap font-sans border border-border/40">{draft.body}</pre>
          )}
          {typeof draft.next_step === "string" && (
            <p className="text-xs text-primary mt-3">→ {draft.next_step}</p>
          )}
        </Card>
      )}

      {/* Missing questions */}
      {s?.missing_questions && s.missing_questions.length > 0 && (
        <Card className="p-5 border-amber-500/30" data-testid="goal-questions">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="h-4 w-4 text-amber-400" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">
              Questions ({s.missing_questions.filter(q => !q.answered).length} restantes / {s.missing_questions.length})
            </h4>
          </div>
          <div className="space-y-3">
            {s.missing_questions.map(q => (
              q.answered ? (
                <div key={q.id} className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30" data-testid={`question-${q.id}-answered`}>
                  <div className="flex items-start gap-2 mb-1">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground line-through">{q.question}</p>
                  </div>
                  <div className="pl-6 mt-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-400/80 mb-0.5">Ta réponse · prise en compte</p>
                    <p className="text-sm font-medium text-emerald-300">{q.answer}</p>
                  </div>
                </div>
              ) : (
                <div key={q.id} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid={`question-${q.id}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <Badge variant="outline" className={`text-[9px] shrink-0 ${q.priority === "critical" ? "text-red-400 border-red-500/30" : "text-amber-400 border-amber-500/30"}`}>
                      {q.priority}
                    </Badge>
                    <p className="text-sm">{q.question}</p>
                  </div>
                  {q.why_needed && <p className="text-[11px] text-muted-foreground mb-2 italic">{q.why_needed}</p>}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ta réponse..."
                      value={answerMap[q.id] || ""}
                      onChange={(e) => setAnswerMap(prev => ({ ...prev, [q.id]: e.target.value }))}
                      className="text-xs h-8"
                      onKeyDown={(e) => { if (e.key === "Enter") answer(q); }}
                      data-testid={`answer-input-${q.id}`}
                    />
                    <Button size="sm" className="h-8 gap-1 shrink-0" onClick={() => answer(q)} disabled={answeringId === q.id || !answerMap[q.id]?.trim()} data-testid={`answer-send-${q.id}`}>
                      {answeringId === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      Envoyer
                    </Button>
                  </div>
                </div>
              )
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent = "text-primary" }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-lg bg-background/60 border border-border/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-base font-bold ${accent}`}>{value}</p>
    </div>
  );
}
